import {useMap, useMapsLibrary} from '@vis.gl/react-google-maps';
import {useEffect, useState} from 'react';

import {OverlayJSON} from '../types';

/**
 * Custom hook for managing Google Maps drawing functionality
 * @param initialValue - Initial DrawingManager instance (optional)
 * @returns An object containing the DrawingManager instance and an array of overlay JSONs
 */
export function useDrawingManager(initialOverlays?: OverlayJSON[]) {
  const map = useMap();
  const drawing = useMapsLibrary('drawing');

  const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | null>();
  const [overlaysJSON, setOverlaysJSON] = useState<OverlayJSON[]>(initialOverlays || []);

  // Initialize the DrawingManager
  useEffect(() => {
    if (!map || !drawing) return;

    // https://developers.google.com/maps/documentation/javascript/reference/drawing
    const newDrawingManager = new drawing.DrawingManager({
      map,
      drawingMode: google.maps.drawing.OverlayType.CIRCLE,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.CIRCLE,
          google.maps.drawing.OverlayType.POLYGON,
          google.maps.drawing.OverlayType.POLYLINE,
          google.maps.drawing.OverlayType.RECTANGLE,
        ],
      },
      markerOptions: {
        draggable: true,
      },
      circleOptions: {
        editable: true,
      },
      polygonOptions: {
        editable: true,
        draggable: true,
      },
      rectangleOptions: {
        editable: true,
        draggable: true,
      },
      polylineOptions: {
        editable: true,
        draggable: true,
      },
    });

    // https://developers.google.com/maps/documentation/javascript/reference/drawing#DrawingManager.addListener
    newDrawingManager.addListener('overlaycomplete', (event: google.maps.drawing.OverlayCompleteEvent) => {
      if (event.overlay) {
        const overlayJSON = convertOverlayToJSON(event.overlay as google.maps.MVCObject, event.type);
        setOverlaysJSON(prevOverlays => [...prevOverlays, overlayJSON]);
      }
    });

    setDrawingManager(newDrawingManager);

    return () => {
      newDrawingManager.setMap(null);
    };
  }, [drawing, map]);

  const convertOverlayToJSON = (overlay: google.maps.MVCObject, type: google.maps.drawing.OverlayType): OverlayJSON => {
    switch (type) {
      case google.maps.drawing.OverlayType.MARKER: {
        const marker = overlay as google.maps.Marker;
        return {
          type: 'marker',
          coordinates: [[marker.getPosition()!.lat(), marker.getPosition()!.lng()]],
        };
      }
      case google.maps.drawing.OverlayType.CIRCLE: {
        const circle = overlay as google.maps.Circle;
        return {
          type: 'circle',
          coordinates: [[circle.getCenter()!.lat(), circle.getCenter()!.lng()], [circle.getRadius()]],
        };
      }
      case google.maps.drawing.OverlayType.POLYGON: {
        const polygon = overlay as google.maps.Polygon;
        return {
          type: 'polygon',
          coordinates: polygon
            .getPath()
            .getArray()
            .map(latLng => [latLng.lat(), latLng.lng()]),
        };
      }
      case google.maps.drawing.OverlayType.POLYLINE: {
        const polyline = overlay as google.maps.Polyline;
        return {
          type: 'polyline',
          coordinates: polyline
            .getPath()
            .getArray()
            .map(latLng => [latLng.lat(), latLng.lng()]),
        };
      }
      case google.maps.drawing.OverlayType.RECTANGLE: {
        const rectangle = overlay as google.maps.Rectangle;
        const bounds = rectangle.getBounds()!;
        return {
          type: 'rectangle',
          coordinates: [
            [bounds.getNorthEast().lat(), bounds.getNorthEast().lng()],
            [bounds.getSouthWest().lat(), bounds.getSouthWest().lng()],
          ],
        };
      }
      default:
        throw new Error(`Unsupported overlay type: ${type}`);
    }
  };

  // Create an overlay from a JSON object
  const createOverlayFromJSON = (overlayJSON: OverlayJSON): google.maps.MVCObject | null => {
    switch (overlayJSON.type) {
      case 'marker':
        return new google.maps.Marker({
          position: new google.maps.LatLng(
            overlayJSON.coordinates[0][0] as number,
            overlayJSON.coordinates[0][1] as number
          ),
          draggable: true,
        });
      case 'circle':
        return new google.maps.Circle({
          center: new google.maps.LatLng(
            overlayJSON.coordinates[0][0] as number,
            overlayJSON.coordinates[0][1] as number
          ),
          radius: overlayJSON.coordinates[1][0] as number,
          editable: true,
        });
      case 'polygon':
      case 'polyline': {
        const PathClass = overlayJSON.type === 'polygon' ? google.maps.Polygon : google.maps.Polyline;
        return new PathClass({
          path: overlayJSON.coordinates.map(coord => new google.maps.LatLng(coord[0] as number, coord[1] as number)),
          editable: true,
          draggable: true,
        });
      }
      case 'rectangle':
        return new google.maps.Rectangle({
          bounds: new google.maps.LatLngBounds(
            new google.maps.LatLng(overlayJSON.coordinates[1][0] as number, overlayJSON.coordinates[1][1] as number),
            new google.maps.LatLng(overlayJSON.coordinates[0][0] as number, overlayJSON.coordinates[0][1] as number)
          ),
          editable: true,
          draggable: true,
        });
      default:
        return null;
    }
  };

  return {drawingManager, overlaysJSON, createOverlayFromJSON};
}
